import React, { useState, useEffect, useContext } from 'react';
import {
  Table,
  Input,
  Button,
  Modal,
  Space,
  message,
  Typography,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  SwapOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import api from '../../api/axios';
import { AuthContext } from '../../context/AuthContext';

const { Title } = Typography;

const AcceptSparePart = () => {
  const { user } = useContext(AuthContext);
  const userWareId = user?.warehouseId;

  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [items, setItems] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();

  // ✅ Fetch all requests where current warehouse is destination
  const fetchRequests = async () => {
    try {
      const res = await api.get(`/api/spare-requests?destinationWarehouseId=${userWareId}`);
      setRequests(res.data);
      setFilteredRequests(res.data);
    } catch (err) {
      console.error(err);
      messageApi.error('Failed to fetch requests');
    }
  };

  useEffect(() => {
    if (userWareId) fetchRequests();
  }, [userWareId]);

  // ✅ Filter requests by salesOrderNumber
  useEffect(() => {
    const filtered = requests.filter(r =>
      r.salesOrderNumber?.toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredRequests(filtered);
  }, [searchText, requests]);

  // ✅ Open modal and prepare item list (no input, just view)
 const openModal = (request) => {
  setSelectedRequest(request);

 const preparedItems = request.items.map(i => ({
    sparepartId: i.sparepartId,
    importerId: i.importerId, 
  partName: i.partName,
  partNumber: i.partNumber,
  requestedQty: i.quantity || i.requestedQty,
  approvedQty: i.quantity || i.requestedQty,
  sourceWarehouseId: request.sourceWarehouse?.id,
  status: i.status
}));

  setItems(preparedItems);
  setModalVisible(true);
};


  // ✅ Copy to clipboard
  const handleCopyOrderNumber = (salesOrderNumber) => {
    navigator.clipboard.writeText(salesOrderNumber);
    messageApi.success(`Order number ${salesOrderNumber} copied!`);
  };

  // ✅ Submit transfer
const handleTransferSubmit = async () => {
  try {
    const payload = {
      destinationWarehouseId: userWareId,
      salesOrderNumber: selectedRequest.salesOrderNumber,
      items: items.map(i => ({
        sparepartId: i.sparepartId,
        approvedQty: i.approvedQty,
        sourceWarehouseId: i.sourceWarehouseId,
      })),
    };
    console.log('Transfer payload:', JSON.stringify(payload, null, 2));

    await api.post('/api/spare-requests/spare-transfers', payload);
    messageApi.success('Spare parts transferred successfully!');
    setModalVisible(false);
    fetchRequests();

    printDeliveryNote({
      warehouseName: user.warehouseName,
      salesOrderNumber: selectedRequest.salesOrderNumber,
      items,
    });
  } catch (err) {
    console.error(err);

    // ✅ Show backend error message if available
    const errorMessage =
      err?.response?.data?.message || 'Failed to transfer spare parts';
    messageApi.error(errorMessage);
  }
};


  // ✅ Printable delivery note
  const printDeliveryNote = ({ warehouseName, salesOrderNumber, items }) => {
    const today = new Date().toLocaleDateString();
    const html = `
      <html>
        <head>
          <title>Delivery Note</title>
          <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
          <style>
            body { font-family: Arial; padding: 10px; font-size: 12px; }
            h2, h3 { text-align: center; margin: 5px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #000; padding: 4px; text-align: left; }
            th { background-color: #f0f0f0; }
            .footer { margin-top: 10px; display: flex; justify-content: space-between; }
            .footer div { text-align: center; width: 30%; }
            .barcode { text-align: center; margin-top: 10px; }
          </style>
        </head>
        <body>
          <h2>JS CONSTRUCTION MACHIERY SPARE PARTS</h2>
          <h3>Delivery Note</h3>
          <p><strong>Date:</strong> ${today}</p>
          <p><strong>Sales Order Number:</strong> ${salesOrderNumber}</p>

          <table>
            <thead>
              <tr>
                <th>Importer ID</th>
                <th>Part Number</th>
                <th>Part Name</th>
                <th>Requested Qty</th>
                <th>Approved Qty</th>
              </tr>
            </thead>
            <tbody>
              ${items.map(i => `
                <tr>
                 <td>${i.importerId}</td>
                  <td>${i.partNumber}</td>
                  <td>${i.partName}</td>
                  <td>${i.requestedQty}</td>
                  <td>${i.approvedQty}</td>
                </tr>`).join('')}
            </tbody>
          </table>

          <div class="footer">
            <div>Prepared By<br/>_____________<br/>Date</div>
            <div>Checked By<br/>_____________<br/>Date</div>
            <div>Approved By<br/>_____________<br/>Date</div>
          </div>

          <div class="barcode">
            <svg id="barcode"></svg>
          </div>

          <script>
            JsBarcode("#barcode", "${salesOrderNumber}", {
              format: "CODE128",
              displayValue: false,
              width: 2,
              height: 50
            });
          </script>
        </body>
      </html>
    `;
    const newWin = window.open('', '_blank');
    newWin.document.write(html);
    newWin.document.close();
    newWin.focus();
    newWin.print();
  };

  // ✅ Table Columns
  const columns = [
    {
      title: 'Order Number',
      dataIndex: 'salesOrderNumber',
      key: 'salesOrderNumber',
      render: (text) => (
        <Space>
          {text}
          <Tooltip title="Copy order number">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => handleCopyOrderNumber(text)}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
  title: 'Request Date',
  dataIndex: 'createdAt',
  key: 'createdAt',
  render: (value) => new Date(value).toLocaleDateString()
}
,
    { title: 'From Warehouse', dataIndex: ['sourceWarehouse', 'name'], key: 'sourceWarehouse' },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button icon={<SwapOutlined />} onClick={() => openModal(record)}>
          Accept
        </Button>
      ),
    },
  ];

  const modalColumns = [
    { title: 'Part Name', dataIndex: 'partName', key: 'partName' },
    { title: 'Part Number', dataIndex: 'partNumber', key: 'partNumber' },
    { title: 'Requested Qty', dataIndex: 'requestedQty', key: 'requestedQty' },
    { title: 'Approved Qty', dataIndex: 'approvedQty', key: 'approvedQty' },
  ];

  return (
    <div style={{ padding: 16 }}>
      {contextHolder}
      <Title level={4}>Accept Spare Parts</Title>

      <Space style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search by Sales Order Number"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          style={{ width: 300 }}
        />
      </Space>

      <Table
        columns={columns}
        dataSource={filteredRequests}
        rowKey={(record) => record.salesOrderNumber}
        pagination={{ pageSize: 10 }}
      />
<Modal
  title={
    <Space>
      Transfer Items for <b>{selectedRequest?.salesOrderNumber}</b>
      <Tooltip title="Copy order number">
        <Button
          type="text"
          icon={<CopyOutlined />}
          onClick={() =>
            handleCopyOrderNumber(selectedRequest?.salesOrderNumber)
          }
        />
      </Tooltip>
    </Space>
  }
  open={modalVisible}
  onCancel={() => setModalVisible(false)}
  onOk={handleTransferSubmit}
  okText="Confirm Transfer"
  okButtonProps={{
    disabled: !items.some(i => i.status === 'sent'), // disable if no sent items
  }}
  width={700}
>
  <Table
    columns={[
       {
        title: 'Importer Id',
        dataIndex: 'importerId',
        key: 'importerId',
        render: (text, record) => record.status === 'delivered' ? <del style={{ color: 'red' }}>{text}</del> : text
      },
      {
        title: 'Part Name',
        dataIndex: 'partName',
        key: 'partName',
        render: (text, record) => record.status === 'delivered' ? <del style={{ color: 'red' }}>{text}</del> : text
      },
      {
        title: 'Part Number',
        dataIndex: 'partNumber',
        key: 'partNumber',
        render: (text, record) => record.status === 'delivered' ? <del style={{ color: 'red' }}>{text}</del> : text
      },
      {
        title: 'Requested Qty',
        dataIndex: 'requestedQty',
        key: 'requestedQty',
        render: (text, record) => record.status === 'delivered' ? <del style={{ color: 'red' }}>{text}</del> : text
      },
      {
        title: 'Approved Qty',
        dataIndex: 'approvedQty',
        key: 'approvedQty',
        render: (text, record) => record.status === 'delivered' ? <del style={{ color: 'red' }}>{text}</del> : text
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (text) =>
          text === 'delivered' ? (
            <span style={{ color: 'red', fontWeight: 'bold' }}>Delivered</span>
          ) : (
            <span>{text}</span>
          ),
      },
    ]}
    dataSource={items}
    rowKey={(record) => record.sparepartId}
    pagination={false}
  />
</Modal>  
    </div>
  );
};

export default AcceptSparePart;
